import { api } from './client'

export type ImportRosterResultDto = {
  dryRun: boolean
  totalRows: number
  companiesCreated: number
  companiesMatched: number
  studentsCreated: number
  studentsSkipped: number
  errorRows: number
  errors: { row: number; message: string }[]
}

export type ClientDto = {
  id: string
  name: string
  contactName: string | null
  contactEmail: string
  phone: string | null
  addressLine1: string | null
  addressLine2: string | null
  city: string | null
  county: string | null
  postcode: string | null
  country: string | null
  notes: string | null
  createdAt: string
  isActive: boolean
}

export type ClientSummaryDto = {
  id: string
  name: string
  contactName: string | null
  contactEmail: string
  phone: string | null
  city: string | null
  county: string | null
  notes: string | null
  createdAt: string
  staffCount: number
  bookingCount: number
  totalInvoiced: number
  outstandingAmount: number
  lastBookingDate: string | null
  isActive: boolean
}
/** Client-portal switches: tenant defaults, and a client's overrides (null = use the default). */
export type PortalPermissions = {
  feedback: boolean
  staffManagement: boolean
  bookingRequests: boolean
  invoices: boolean
  editDetails: boolean
  assignAttendees: boolean
  bookSessions: boolean
}
export type PortalOverrides = { [K in keyof PortalPermissions]: boolean | null }
export type ClientPortalSettings = { defaults: PortalPermissions; overrides: PortalOverrides; effective: PortalPermissions }

export type PhotoSettings = { enabled: boolean; notice: string | null; showOnVerification: boolean }
export type DataProcessingStatus = { currentVersion: number; acceptedVersion: number | null; acceptedAt: string | null; acceptedBy: string | null; upToDate: boolean }

export type StaffMemberDto = { id: string; firstName: string; lastName: string; email: string; role: string; isActive: boolean; invitePending: boolean; photoUpdatedAt: string | null }
export type SignatureType = 'None' | 'Image' | 'Text'

export type CertificateFieldConfig = {
  field: 'recipientName' | 'courseName' | 'courseCode' | 'completedDate' | 'expiryDate' | 'companyName' | 'static' | 'companyLogo' | 'image' | 'verificationQr' | 'trainerSignature' | 'companySignature'
  elementId?: string   // unique ID for static/image elements (allows multiples)
  staticText?: string  // verbatim text when field == 'static'
  x: number        // 0–100 percentage of image width
  y: number        // 0–100 percentage of image height
  fontSize: number // points relative to 1000px height reference; unused for companyLogo/image/signatures
  bold: boolean
  align: 'left' | 'center' | 'right'
  color: string    // hex e.g. "#1a1a1a"; used for printed name under signatures
  dateFormat?: 'short' | 'medium' | 'long' | 'full' | 'month-year'
  dateOrdinal?: boolean
  logoWidth?: number   // companyLogo / trainerSignature / companySignature: width as % of cert width (default 20)
  imageUrl?: string    // image only: URL of the certificate image asset
  imageWidth?: number  // image only: width as % of cert width (default 20)
  fontFamily?: string  // font family: "Inter" | "Montserrat" | "Lora" | "Playfair Display" | "Cinzel" (default: Inter)
}

export type TrainerSignatureDto = {
  signatureType: SignatureType
  signatureText: string | null
  signatureImageUrl: string | null
}

export type CertificateImageDto = {
  id: string
  name: string
  url: string
  createdAt: string
}

export type CertificateTemplateDto = {
  id: string
  name: string
  description: string | null
  backgroundImageUrl: string | null
}

export type SystemTemplateDto = {
  id: string
  name: string
  description: string | null
  templateType: 'Png' | 'Svg'
  thumbnailUrl: string | null
  createdAt: string
  isPortrait: boolean
}

export type CertificateDesignDto = {
  id: string
  name: string
  // Tenant-uploaded template (null when using a system template)
  certificateTemplateId: string | null
  templateName: string | null
  templateBackgroundImageUrl: string | null
  // System template (null when using a tenant-uploaded template)
  systemCertificateTemplateId: string | null
  systemTemplateName: string | null
  systemTemplateThumbnailUrl: string | null
  primaryColour: string | null
  backgroundColour: string | null
  fieldsJson: string | null
  courseCount: number
  isPortrait: boolean
}

export type CoursePricingModel = 'PerPerson' | 'FlatFee'

export type CourseDto = {
  id: string
  name: string
  code: string | null
  /** The category's name (or the old free text on a course not yet migrated). Read only: save with categoryId. */
  category: string | null
  categoryId: string | null
  /** The web address on the customer's website, and whether the course is shown there (Pro). Read only. */
  slug: string | null
  showOnWebsite: boolean
  description: string | null
  websiteUrl: string | null
  price: number | null
  /** Legacy hours; use durationMinutes. Not sent when saving. */
  durationHours: number | null
  durationMinutes: number | null
  defaultDays: number
  durationLabel: string | null
  defaultExpiryMonths: number | null
  certificateDesignId: string | null
  certificateDesignName: string | null
  isThirdPartyCertified: boolean
  thirdPartyProviderName: string | null
  sendThirdPartyCompletionEmail: boolean
  pricingModel: CoursePricingModel
  flatFeeAmount: number | null
  flatFeeCapacity: number | null
  requirePhotoCheck: boolean
}

export type WebsiteImageDto = {
  id: string
  altText: string
  /** Where to keep the picture when it is cropped, as a percentage from the top-left. */
  focusX: number
  focusY: number
  width: number
  height: number
  sortOrder: number
}

export type CourseCategoryDto = {
  id: string
  name: string
  slug: string
  description: string | null
  sortOrder: number
  courseCount: number
  image: WebsiteImageDto | null
}

export type CourseWebsiteDto = {
  courseId: string
  courseName: string
  showOnWebsite: boolean
  slug: string | null
  suggestedSlug: string
  slugLocked: boolean
  overview: string | null
  whoItsFor: string | null
  whatYoullLearn: string[]
  requirements: string[]
  whatsIncluded: string[]
  images: WebsiteImageDto[]
}

export type CourseWebsiteSave = Omit<CourseWebsiteDto, 'courseId' | 'courseName' | 'suggestedSlug' | 'slugLocked' | 'images'>

/** The public address of a website picture at (about) the given width: a stored size at least that wide, else the largest. */
export const websiteImageUrl = (id: string, width?: number) =>
  `${api.defaults.baseURL}/public/website-images/${id}${width ? `?w=${width}` : ''}`

/** What the course form sends: a course without the fields the server works out or that have their own screen. */
export type CourseSave = Omit<CourseDto, 'id' | 'certificateDesignName' | 'durationHours' | 'slug' | 'showOnWebsite'>

export type PendingThirdPartyCertificateDto = {
  trainingRecordId: string
  courseId: string
  courseName: string
  providerName: string
  staffMemberId: string
  staffMemberName: string
  clientName: string
  completedDate: string
}

export type PendingThirdPartySessionCertificateDto = {
  sessionAttendeeId: string
  fullName: string
  completedDate: string
}

export type AssignedThirdPartyCertificateDto = {
  trainingRecordId: string
  courseId: string
  courseName: string
  staffMemberId: string
  staffMemberName: string
  clientName: string
  completedDate: string
  certificateId: string
  uploadedAt: string
}

export type CourseMaterialDto = {
  id: string
  courseId: string
  title: string
  materialType: 'File' | 'Link'
  fileName: string | null
  contentType: string | null
  fileSizeBytes: number | null
  url: string | null
  sortOrder: number
  createdAt: string
}

export type TrainerRole = 'Lead' | 'Assistant'

export type TrainerDto = {
  id: string
  firstName: string
  lastName: string
  email: string
  phone: string | null
  isActive: boolean
  hasAccount: boolean
  qualifiedCourseIds: string[]
}

export type TrainerBookingDto = {
  bookingId: string
  courseName: string
  courseCode: string | null
  clientName: string
  bookingDate: string
  status: string
  role: string
  places: number
  venueName: string | null
}

export type TrainerSeatInfoDto = {
  used: number
  total: number
  base: number
  addon: number
  /** Free seats granted by CertSlate (not on any bill). */
  complimentary: number
}

export type CalendarTrainerDto = {
  trainerId: string
  firstName: string
  lastName: string
  role: TrainerRole
}

export type CalendarBookingDto = {
  id: string
  eventType: 'Booking' | 'Session'
  clientId: string | null
  bookingDate: string
  clientName: string | null
  courseName: string
  status: BookingStatus | 'Draft' | 'Published'
  places: number
  attendeeCount: number
  leadTrainerName: string | null
  trainers: CalendarTrainerDto[]
  dayNumber: number | null
  totalDays: number | null
}

export type TrainerConflictDto = {
  bookingId: string
  clientName: string
  courseName: string
  bookingDate: string
  kind: 'Booking' | 'Unavailable'
  reason: string | null
}

export type TrainerUnavailabilityDto = {
  id: string
  startDate: string
  endDate: string
  reason: string | null
  recurrenceGroupId: string | null
}

export type DayOfWeekName = 'Sunday' | 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday'
export type TrainingRecordDto = { id: string; courseId: string; courseName: string; completedDate: string; expiryDate: string | null; status: 'Valid' | 'DueSoon' | 'Overdue' | 'NoExpiry'; hasCertificate: boolean; courseIsThirdPartyCertified: boolean; certificateId: string | null; isSuperseded: boolean }
export type ComplianceReportDto = { clientId: string; clientName: string; totalStaff: number; valid: number; dueSoon: number; overdue: number; noExpiry: number }

export type NotificationPreferencesDto = {
  expiryReminders: boolean
  bookingConfirmations: boolean
  weeklyDigest: boolean
  invoiceAlerts: boolean
}

export type AdminDashboardDto = {
  totalClients: number
  totalCertificatesIssued: number
  totalOverdue: number
  totalDueSoon: number
  pendingEnquiries: number
  unpaidInvoicesCount: number
  clients: ClientSummaryRow[]
  upcomingBookings: UpcomingBookingRow[]
  topCourses: TopCourseRow[]
}
export type ClientSummaryRow = {
  clientId: string
  clientName: string
  contactEmail: string
  contactName: string | null
  totalStaff: number
  overdue: number
  dueSoon: number
  totalCerts: number
  compliancePct: number
}

export type TenantEmailTemplateDto = {
  type: string
  subject: string
  body: string
  isCustomised: boolean
}
export type UpcomingBookingRow = {
  bookingId: string
  clientName: string
  courseName: string
  bookingDate: string
  places: number
  status: string
}
export type TopCourseRow = {
  courseId: string
  courseName: string
  bookingCount: number
  totalPlaces: number
}

export type InvoiceListDto = {
  id: string
  invoiceNumber: string
  issuedDate: string
  dueDate: string | null
  amount: number
  paymentStatus: PaymentStatus
  notes: string | null
  clientId: string
  clientName: string
  /** "First Aid" for a single-booking invoice, "First Aid +2 more" for a bundle. */
  courseSummary: string
  bookingCount: number
  xeroInvoiceId: string | null
  xeroSyncedAt: string | null
  currency: string
}

export type BulkPushResult = {
  succeeded: number
  skipped: number
  failed: number
  errors: string[]
}

export type BookingListDto = {
  id: string
  bookingDate: string
  places: number
  status: BookingStatus
  notes: string | null
  clientId: string
  clientName: string
  courseId: string
  courseName: string
  courseCode: string | null
  rejectionReason: string | null
  certificatesIssuedAt: string | null
  attendeeCount: number
  leadTrainerName: string | null
  additionalTrainerCount: number
  invoiceId: string | null
  invoiceNumber: string | null
  invoicePaymentStatus: PaymentStatus | null
  additionalDaysCount: number
}

export type InvoiceLineDto = {
  bookingId: string
  bookingDate: string
  courseName: string
  courseCode: string | null
  places: number
  pricingModel: CoursePricingModel
  unitPrice: number | null
  flatFeeCapacity: number | null
  amount: number
}

export type InvoiceDetailDto = {
  id: string
  invoiceNumber: string
  issuedDate: string
  dueDate: string | null
  amount: number
  vatRate: number | null
  vatAmount: number | null
  total: number
  paymentStatus: PaymentStatus
  notes: string | null
  xeroInvoiceId: string | null
  xeroSyncedAt: string | null
  lines: InvoiceLineDto[]
  clientId: string
  clientName: string
  clientContactName: string | null
  clientContactEmail: string
  clientAddressLine1: string | null
  clientAddressLine2: string | null
  clientCity: string | null
  clientCounty: string | null
  clientPostcode: string | null
  clientCountry: string | null
  companyDisplayName: string | null
  companyLogoUrl: string | null
  companyAddressLine1: string | null
  companyAddressLine2: string | null
  companyCity: string | null
  companyCounty: string | null
  companyPostcode: string | null
  companyCountry: string | null
  companyEmail: string | null
  companyWebsite: string | null
  companyVatNumber: string | null
  companyNumber: string | null
  companyBankName: string | null
  companyAccountName: string | null
  companyAccountNumber: string | null
  companySortCode: string | null
  companyIban: string | null
  companyPaymentReference: string | null
  companyPaymentLink: string | null
  stripeConnectEnabled: boolean
  currency: string
  taxLabel: string
  companyRoutingNumber: string | null
  region: string
}

export type BookingStatus = 'Booked' | 'Completed' | 'Cancelled' | 'Rejected'
export type PaymentStatus = 'Unpaid' | 'PartiallyPaid' | 'Paid' | 'Void'
export type EnquiryStatus = 'Pending' | 'Acknowledged' | 'Converted' | 'Declined'

export type SupportRequestCategory = 'Question' | 'Problem' | 'Billing' | 'FeatureRequest' | 'Other' | 'CallRequest'
export type SupportRequestStatus = 'Open' | 'InProgress' | 'Resolved'

export type MySupportRequestDto = {
  id: string
  subject: string
  message: string
  category: SupportRequestCategory
  status: SupportRequestStatus
  createdAt: string
  resolvedAt: string | null
}

export type BookingEnquiryDto = {
  id: string
  clientName: string
  clientId: string
  courseName: string
  courseId: string | null
  hasCourse: boolean
  preferredDate: string | null
  places: number
  message: string | null
  status: EnquiryStatus
  adminNotes: string | null
  createdAt: string
  requestedStaffNames: string[]
}

export type VenueDto = {
  id: string
  name: string
  addressLine1: string | null
  addressLine2: string | null
  city: string | null
  county: string | null
  postcode: string | null
  notes: string | null
  clientId: string | null
}

export type VenueSummaryDto = {
  id: string
  name: string
  city: string | null
  postcode: string | null
}

export type SessionDayDto = {
  startsAt: string
  endsAt: string | null
}

export type SessionTrainerAssignmentDto = {
  trainerId: string
  firstName: string
  lastName: string
  role: TrainerRole
}

export type DeliveryMethod = 'InPerson' | 'Online'

export type CourseSessionDto = {
  id: string
  courseId: string
  courseName: string
  courseDescription: string | null
  courseIsThirdPartyCertified: boolean
  courseThirdPartyProviderName: string | null
  venue: VenueSummaryDto | null
  startsAt: string
  endsAt: string | null
  totalPlaces: number
  bookedPlaces: number
  availablePlaces: number
  price: number | null
  pricingModel: CoursePricingModel
  flatFeeAmount: number | null
  flatFeeCapacity: number | null
  status: 'Draft' | 'Published' | 'Cancelled' | 'Completed'
  notes: string | null
  createdAt: string
  certificatesIssuedAt: string | null
  additionalDays: SessionDayDto[]
  trainerAssignments: SessionTrainerAssignmentDto[]
  deliveryMethod: DeliveryMethod
  /** Only populated for admin queries — never sent on the public listing/detail. */
  meetingUrl: string | null
  meetingInstructions: string | null
}

export type SessionBookingDto = {
  id: string
  contactName: string
  contactEmail: string
  contactPhone: string | null
  placesRequested: number
  status: 'Confirmed' | 'Cancelled' | 'PendingPayment'
  notes: string | null
  adminNotes: string | null
  createdAt: string
  paidAt: string | null
  /** Added by an admin on the session page rather than through the public booking form. */
  addedByAdmin: boolean
  clientName: string | null
}

export type SessionDayRequest = {
  startsAt: string
  endsAt: string | null
}

export type SessionTrainerRequest = {
  trainerId: string
  role: TrainerRole
}

export type SessionRequest = {
  courseId: string
  venueId: string | null
  startsAt: string
  endsAt: string | null
  totalPlaces: number
  price: number | null
  pricingModel: CoursePricingModel
  flatFeeAmount: number | null
  flatFeeCapacity: number | null
  status: string
  notes: string | null
  additionalDays: SessionDayRequest[]
  trainerAssignments: SessionTrainerRequest[]
  deliveryMethod: DeliveryMethod
  meetingUrl: string | null
  meetingInstructions: string | null
}

export type BookingAttendeeDto = {
  attendeeId: string
  staffMemberId: string
  firstName: string
  lastName: string
  attendanceStatus: string
  outcome: string | null
  isWalkIn: boolean
  identityCheckedAt: string | null
  identityCheckedBy: string | null
  identityCheckedByAdmin: boolean
}
export type BookingActivityDto = { at: string; by: string | null; text: string }
export type BookingAttendeeCertificateDto = {
  staffMemberId: string
  trainingRecordId: string | null
  hasCertificate: boolean
  certificateId: string | null
  certificateUploadedAt: string | null
}
export type BookingTrainerAssignmentDto = { trainerId: string; firstName: string; lastName: string; role: TrainerRole }
export type BookingInvoiceDto = {
  id: string
  invoiceNumber: string
  issuedDate: string
  dueDate: string | null
  amount: number
  paymentStatus: PaymentStatus
  notes: string | null
  /** How many bookings this invoice covers — >1 means this one is bundled with others. */
  bookingCount: number
  currency: string
}
export type BookingDayDto = {
  date: string
  startTime: string | null
  endTime: string | null
}

export type BookingDto = {
  id: string
  clientId: string
  clientName: string
  courseId: string
  courseName: string
  courseCode: string | null
  courseIsThirdPartyCertified: boolean
  courseThirdPartyProviderName: string | null
  bookingDate: string
  startTime: string | null
  endTime: string | null
  places: number
  status: BookingStatus
  notes: string | null
  rejectionReason: string | null
  certificatesIssuedAt: string | null
  venueId: string | null
  venue: VenueDto | null
  attendees: BookingAttendeeDto[]
  trainerAssignments: BookingTrainerAssignmentDto[]
  invoice: BookingInvoiceDto | null
  additionalDays: BookingDayDto[]
  deliveryMethod: DeliveryMethod
  meetingUrl: string | null
  meetingInstructions: string | null
}
export type XeroConnectionDto = {
  organisationName: string
  accountCode: string
  connectedAt: string
  outputTaxType: string | null
}

export type StripeConnectionDto = {
  stripeAccountId: string
  chargesEnabled: boolean
  onboardingComplete: boolean
  connectedAt: string
}

export type PaymentListDto = {
  id: string
  source: 'Invoice' | 'SessionBooking'
  invoiceId: string | null
  sessionBookingId: string | null
  description: string
  payer: string | null
  amount: number
  refundedAmount: number
  currency: string
  createdAt: string
}

export type CompanyProfileDto = {
  displayName: string | null
  logoUrl: string | null
  primaryColour: string | null
  secondaryColour: string | null
  headerStyle: string
  cornerStyle: string
  fontFamily: string
  companyNumber: string | null
  vatNumber: string | null
  addressLine1: string | null
  addressLine2: string | null
  city: string | null
  county: string | null
  postcode: string | null
  country: string | null
  contactEmail: string | null
  website: string | null
  bankName: string | null
  accountName: string | null
  accountNumber: string | null
  sortCode: string | null
  iban: string | null
  paymentTermsDays: number | null
  paymentReference: string | null
  adminReminderDays: number[] | null
  clientReminderDays: number[] | null
  planTier: string | null
  slug: string | null
  signatureType: SignatureType
  signatureText: string | null
  signatureImageUrl: string | null
  feedbackEnabled: boolean
  feedbackTrainerVisible: boolean
  feedbackAllowEdit: boolean
  feedbackWindowDays: number | null
  feedbackNotifyClient: boolean
  defaultVatRate: number | null
  paymentLink: string | null
  acceptInvoicePayments: boolean
  acceptSessionPayments: boolean
  requireSessionPaymentToBook: boolean
  passFeeToCustomerForInvoices: boolean
  passFeeToCustomerForSessions: boolean
  termsVersion: number
  termsUpdatedAt: string | null
  /** Read-only here — set by SuperAdmin. */
  currency: string
  taxLabel: string
  routingNumber: string | null
  /** Tenant region (ISO country) and time zone - read-only, set by SuperAdmin. */
  region: string
  timeZoneId: string
  /** CertSlate Live is switched on for this tenant (set by SuperAdmin). */
  liveDeliveryEnabled: boolean
}

export type BookingFeedbackDto = {
  id: string
  bookingId: string
  clientName: string
  courseName: string
  bookingDate: string
  rating: number
  comment: string | null
  submittedAt: string
  updatedAt: string | null
}

export type FeedbackSettingsDto = {
  enabled: boolean
  trainerVisible: boolean
  allowEdit: boolean
  windowDays: number | null
  notifyClient: boolean
}

// Live sessions as seen from Feedback and analytics
export type LiveInsightDto = {
  sessions: number
  participants: number
  feedbackAverage: number | null
  feedbackResponses: number
  learningGain: number | null
  confidenceChange: number | null
}

export type LiveCommentGroupDto = { question: string; comments: string[] }

export type LiveSessionInsightDto = {
  deliveryId: string
  bookingId: string | null
  sessionId: string | null
  courseId: string
  courseName: string
  clientName: string | null
  date: string
  trainers: string[]
  participants: number
  feedbackAverage: number | null
  feedbackCount: number
  smallSample: boolean
  preAverage: number | null
  postAverage: number | null
  learningGain: number | null
  confidenceBefore: number | null
  confidenceAfter: number | null
  confidenceChange: number | null
  comments: LiveCommentGroupDto[]
}

// Analytics
export type AnalyticsOverviewDto = {
  totalRevenue: number
  outstandingAmount: number
  totalBookings: number
  completedBookings: number
  cancelledBookings: number
  averageRating: number | null
  totalFeedback: number
  totalCertificates: number
  overdueInvoices: number
  topClientName: string | null
  topClientRevenue: number | null
  live?: LiveInsightDto | null
}

export type CourseAnalyticsDto = {
  courseId: string
  courseName: string
  courseCode: string | null
  totalBookings: number
  completedBookings: number
  cancelledBookings: number
  revenue: number
  averageRating: number | null
  feedbackCount: number
  certificatesIssued: number
  live?: LiveInsightDto | null
}

export type TrainerAnalyticsDto = {
  trainerId: string
  trainerName: string
  sessionsDelivered: number
  revenue: number
  averageRating: number | null
  feedbackCount: number
  certificatesIssued: number
  live?: LiveInsightDto | null
}

export type ClientAnalyticsDto = {
  clientId: string
  clientName: string
  totalBookings: number
  completedBookings: number
  totalRevenue: number
  outstandingAmount: number
  certificatesIssued: number
}

export type MonthlyRevenueDto = {
  year: number
  month: number
  monthLabel: string
  revenue: number
  invoiceCount: number
}

export type FinanceAnalyticsDto = {
  monthlyRevenue: MonthlyRevenueDto[]
  paidInvoices: number
  overdueInvoices: number
  pendingInvoices: number
  totalRevenue: number
  totalOutstanding: number
  averageInvoiceValue: number
  topCoursesByRevenue: { courseName: string; revenue: number }[]
}

export type AppNotificationDto = {
  id: string
  event: string
  title: string
  body: string | null
  entityId: string | null
  entityType: string | null
  isRead: boolean
  createdAt: string
}

export interface BillingInfo {
  planTier: string
  subscriptionStatus: string
  trialEndsAt: string | null
  currentPeriodEndsAt: string | null
  isActive: boolean
  cancelAtPeriodEnd: boolean
  /** A real subscription that isn't already ending - controls the Cancel subscription button. */
  canCancel: boolean
  billingInterval: 'Monthly' | 'Annual'
  /** A monthly, self-serve subscription that can move to yearly now (and yearly prices exist). */
  canSwitchToAnnual: boolean
  /** A seat change or upgrade Stripe is holding until the customer pays (e.g. their bank needs to approve it). */
  hasPendingChange: boolean
  /** Stripe's secure page to finish paying for it. Empty/null if Stripe didn't give one. */
  pendingPaymentUrl: string | null
}

/** Applied straight away, or held by Stripe until the customer pays. */
export interface SubscriptionChangeResult {
  pending: boolean
  paymentUrl: string | null
}

/** The exact pro-rata money effect of a seat or plan change, worked out by Stripe before anything changes. */
export interface ChangePreview {
  /** Positive = a charge, negative = a credit. */
  amountPence: number
  currency: string
  /** Send this back when confirming so the charge matches the amount shown. */
  prorationDate: string
  /** On a free trial nothing is charged now. */
  isTrial: boolean
  /** Yearly: charged to the card straight away. Monthly: added to the next invoice. */
  chargedNow: boolean
}

export interface AnnualSwitchPreview {
  /** What would be charged right now. Always 0 during a trial. */
  amountDueNowPence: number
  currency: string
  isTrial: boolean
}

export interface CancellationOptions {
  canCancel: boolean
  isTrialing: boolean
  /** When access ends if they cancel (end of the trial or the paid period). */
  accessEndsAt: string | null
  trialExtension: { days: number; newTrialEndsAt: string } | null
  discount: { percentOff: number; months: number } | null
  /** Yearly plans: access runs to the end of the paid year and there's no refund. */
  isAnnual: boolean
}

export interface CancellationBody {
  reason: string | null
  reasonDetail: string | null
}

// Public attendees (open-enrolment session bookers)
export type AttendeeListItemDto = {
  id: string
  fullName: string
  email: string
  companyName: string | null
  sessionsCount: number
  certificatesCount: number
  lastBookingAt: string | null
  marketingConsent: boolean
  photoUpdatedAt: string | null
  /** Set for someone added from a client's staff: the client they belong to. */
  clientStaffOf: string | null
}

export type AttendeeBookingDto = {
  sessionAttendeeId: string
  sessionId: string
  courseName: string
  startsAt: string
  bookingStatus: string
  attendanceStatus: string
  bookedAt: string
}

export type AttendeeCertificateDto = {
  certificateId: string
  courseName: string
  issuedAt: string
}

export type AttendeeDetailDto = {
  id: string
  fullName: string
  email: string
  phone: string | null
  companyName: string | null
  marketingConsent: boolean
  marketingConsentAt: string | null
  createdAt: string
  bookings: AttendeeBookingDto[]
  certificates: AttendeeCertificateDto[]
  photoUpdatedAt: string | null
}

export type AttendanceStatus = 'NotRecorded' | 'Present' | 'Absent'
export type AttendanceOutcome = 'Passed' | 'Failed'

export type SessionAttendeeDto = {
  sessionAttendeeId: string
  bookingId: string
  publicAttendeeId: string
  attendeeName: string
  attendeeEmail: string
  companyName: string | null
  bookingContactName: string
  attendanceStatus: AttendanceStatus
  outcome: AttendanceOutcome | null
  hasCertificate: boolean
  certificateId: string | null
  certificateUploadedAt: string | null
  /** Added from a client's staff: their certificate becomes a training record and counts towards that client's compliance. */
  staffMemberId: string | null
  clientName: string | null
  addedByAdmin: boolean
}

export type IssueSessionCertificatesResult = {
  certificatesGenerated: number
  alreadyIssued: number
  skippedNotPresent: number
}

export type AddSessionAttendeesRequest = {
  people: { firstName: string; lastName: string; email: string; phone: string | null; companyName: string | null }[]
  staffMemberIds: string[]
  markPresent: boolean
  sendBookingEmails: boolean
  issueCertificates: boolean
  sendCertificateEmails: boolean
}

export type AddSessionAttendeesResult = {
  added: number
  skipped: { name: string; email: string; reason: string }[]
  certificatesGenerated: number
  /** Set when they were added but certificates couldn't be issued. */
  certificateProblem: string | null
}

export const adminApi = {
  // Clients
  getClients: () => api.get<ClientDto[]>('/clients'),
  getClientSummaries: () => api.get<ClientSummaryDto[]>('/clients/summaries'),
  createPreviewToken: (clientId: string) =>
    api.post<{ token: string; clientName: string }>(`/clients/${clientId}/preview-token`),
  getClient: (id: string) => api.get<ClientDto>(`/clients/${id}`),
  createClient: (data: Omit<ClientDto, 'id' | 'createdAt' | 'isActive'>) => api.post<{ id: string }>('/clients', data),
  updateClient: (id: string, data: Omit<ClientDto, 'id' | 'createdAt' | 'isActive'>) => api.put(`/clients/${id}`, data),
  setClientActive: (id: string, isActive: boolean) => api.put(`/clients/${id}/active`, { isActive }),

  // Staff
  getStaff: (clientId: string) => api.get<StaffMemberDto[]>(`/clients/${clientId}/staff`),
  addStaff: (clientId: string, data: { email: string; firstName: string; lastName: string; role: string }) =>
    api.post<{ id: string }>(`/clients/${clientId}/staff`, data),
  resendStaffInvite: (id: string) => api.post(`/staff/${id}/resend-invite`, {}),
  // Identity photos
  getDataProcessingStatus: () => api.get<DataProcessingStatus>('/tenant-settings/data-processing'),
  acceptDataProcessing: () => api.post('/tenant-settings/data-processing/accept', {}),
  getPhotoSettings: () => api.get<PhotoSettings>('/tenant-settings/photos'),
  updatePhotoSettings: (data: PhotoSettings) => api.put('/tenant-settings/photos', data),
  /** Switches CertSlate Live on or off for this organisation (on by default). */
  updateLiveSettings: (enabled: boolean) => api.put('/tenant-settings/live', { enabled }),
  getStaffPhoto: (id: string) => api.get<Blob>(`/staff/${id}/photo`, { responseType: 'blob' }),
  setStaffPhoto: (id: string, photo: Blob) => { const f = new FormData(); f.append('file', photo, 'photo.jpg'); return api.post(`/staff/${id}/photo`, f) },
  removeStaffPhoto: (id: string) => api.delete(`/staff/${id}/photo`),
  getAttendeePhoto: (id: string) => api.get<Blob>(`/attendees/${id}/photo`, { responseType: 'blob' }),
  setAttendeePhoto: (id: string, photo: Blob) => { const f = new FormData(); f.append('file', photo, 'photo.jpg'); return api.post(`/attendees/${id}/photo`, f) },
  removeAttendeePhoto: (id: string) => api.delete(`/attendees/${id}/photo`),
  getPortalDefaults: () => api.get<PortalPermissions>('/tenant-settings/portal'),
  updatePortalDefaults: (data: Omit<PortalPermissions, 'feedback'>) => api.put('/tenant-settings/portal', data),
  getClientPortalSettings: (clientId: string) => api.get<ClientPortalSettings>(`/clients/${clientId}/portal-settings`),
  updateClientPortalSettings: (clientId: string, overrides: PortalOverrides) => api.put(`/clients/${clientId}/portal-settings`, overrides),
  updateStaff: (id: string, data: { firstName: string; lastName: string; email: string; role: string }) =>
    api.put(`/staff/${id}`, data),
  importStaff: (clientId: string, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return api.post(`/clients/${clientId}/staff/import`, form)
  },

  // Certificate templates
  getCertificateTemplates: () => api.get<CertificateTemplateDto[]>('/certificate-templates'),
  createCertificateTemplate: (data: { name: string; description: string | null }) =>
    api.post<{ id: string }>('/certificate-templates', data),
  updateCertificateTemplate: (id: string, data: { name: string; description: string | null }) =>
    api.put(`/certificate-templates/${id}`, data),
  deleteCertificateTemplate: (id: string) => api.delete(`/certificate-templates/${id}`),
  uploadCertificateTemplateBackground: (id: string, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return api.post<{ backgroundImageUrl: string }>(`/certificate-templates/${id}/background`, form)
  },

  // Certificate images
  getCertificateImages: () => api.get<CertificateImageDto[]>('/certificate-images'),
  uploadCertificateImage: (name: string, file: File) => {
    const form = new FormData()
    form.append('name', name)
    form.append('file', file)
    return api.post<CertificateImageDto>('/certificate-images', form)
  },
  deleteCertificateImage: (id: string) => api.delete(`/certificate-images/${id}`),

  // Certificate designs
  getSystemCertificateTemplates: () =>
    api.get<SystemTemplateDto[]>('/certificate-templates/system'),
  getCertificateDesigns: () => api.get<CertificateDesignDto[]>('/certificate-designs'),
  createCertificateDesign: (data: {
    name: string
    certificateTemplateId: string | null
    systemCertificateTemplateId: string | null
    primaryColour: string | null
    backgroundColour: string | null
    isPortrait: boolean
  }) => api.post<{ id: string }>('/certificate-designs', data),
  updateCertificateDesign: (id: string, data: {
    name: string
    certificateTemplateId: string | null
    systemCertificateTemplateId: string | null
    primaryColour: string | null
    backgroundColour: string | null
    fieldsJson: string | null
    isPortrait: boolean
  }) => api.put(`/certificate-designs/${id}`, data),
  deleteCertificateDesign: (id: string) => api.delete(`/certificate-designs/${id}`),

  // Courses
  getCourses: () => api.get<CourseDto[]>('/courses'),
  getCourse: (id: string) => api.get<CourseDto>(`/courses/${id}`),
  createCourse: (data: CourseSave) => api.post<{ id: string }>('/courses', data),
  updateCourse: (id: string, data: CourseSave) => api.put(`/courses/${id}`, data),
  getCourseCategories: () => api.get<CourseCategoryDto[]>('/course-categories'),
  createCourseCategory: (data: { name: string; description?: string | null }) => api.post<{ id: string }>('/course-categories', data),
  /** description: undefined leaves it as it is, '' clears it. */
  updateCourseCategory: (id: string, data: { name: string; description?: string | null }) => api.put(`/course-categories/${id}`, data),
  deleteCourseCategory: (id: string) => api.delete(`/course-categories/${id}`),
  reorderCourseCategories: (orderedIds: string[]) => api.put('/course-categories/order', { orderedIds }),
  uploadCategoryImage: (categoryId: string, file: File, altText: string) => {
    const form = new FormData()
    form.append('file', file)
    form.append('altText', altText)
    return api.post<WebsiteImageDto>(`/course-categories/${categoryId}/image`, form)
  },

  // Website tab on a course (Pro)
  getCourseWebsite: (courseId: string) => api.get<CourseWebsiteDto>(`/courses/${courseId}/website`),
  updateCourseWebsite: (courseId: string, data: CourseWebsiteSave) => api.put(`/courses/${courseId}/website`, data),
  uploadCourseImage: (courseId: string, file: File, altText: string) => {
    const form = new FormData()
    form.append('file', file)
    form.append('altText', altText)
    return api.post<WebsiteImageDto>(`/courses/${courseId}/website/images`, form)
  },
  reorderCourseImages: (courseId: string, orderedIds: string[]) => api.put(`/courses/${courseId}/website/images/order`, { orderedIds }),
  updateWebsiteImage: (id: string, data: { altText: string; focusX: number; focusY: number }) => api.put(`/website-images/${id}`, data),
  deleteWebsiteImage: (id: string) => api.delete(`/website-images/${id}`),
  deleteCourse: (id: string) => api.delete(`/courses/${id}`),
  setCourseQualifiedTrainers: (id: string, trainerIds: string[]) =>
    api.put(`/courses/${id}/trainers`, { trainerIds }),

  // Course materials
  getCourseMaterials: (courseId: string) =>
    api.get<CourseMaterialDto[]>(`/courses/${courseId}/materials`),
  uploadCourseMaterial: (courseId: string, file: File, title: string, onProgress?: (pct: number) => void) => {
    const form = new FormData()
    form.append('file', file)
    form.append('title', title)
    return api.post<{ id: string }>(`/courses/${courseId}/materials/upload`, form, {
      onUploadProgress: (evt) => {
        if (onProgress && evt.total) onProgress(Math.round((evt.loaded * 100) / evt.total))
      },
    })
  },
  addCourseMaterialLink: (courseId: string, data: { title: string; url: string }) =>
    api.post<{ id: string }>(`/courses/${courseId}/materials/link`, data),
  getCourseMaterialDownloadUrl: (courseId: string, materialId: string) =>
    api.get<{ url: string }>(`/courses/${courseId}/materials/${materialId}/download-url`),
  deleteCourseMaterial: (courseId: string, materialId: string) =>
    api.delete(`/courses/${courseId}/materials/${materialId}`),

  // Training records
  getTrainingRecords: (staffId: string) => api.get<TrainingRecordDto[]>(`/staff/${staffId}/training-records`),
  addTrainingRecord: (staffId: string, data: { courseId: string; completedDate: string; expiryDate: string | null }) =>
    api.post<{ id: string }>(`/staff/${staffId}/training-records`, data),
  updateTrainingRecord: (id: string, data: { courseId: string; completedDate: string; expiryDate: string | null }) =>
    api.put(`/training-records/${id}`, data),

  bulkAddTrainingRecords: (clientId: string, data: {
    courseId: string; completedDate: string; expiryDate: string | null; staffMemberIds: string[]
  }) => api.post<{ trainingRecordsCreated: number; alreadyExisted: number; certificatesGenerated: number }>(
    `/clients/${clientId}/bulk-training-records`, data),

  // Certificates
  uploadCertificate: (trainingRecordId: string, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return api.post(`/training-records/${trainingRecordId}/certificate`, form)
  },
  getPendingThirdPartyCertificates: (courseId?: string, bookingId?: string) =>
    api.get<PendingThirdPartyCertificateDto[]>('/third-party-certificates/pending', { params: { courseId, bookingId } }),
  getAssignedThirdPartyCertificates: (courseId?: string, bookingId?: string) =>
    api.get<AssignedThirdPartyCertificateDto[]>('/third-party-certificates/assigned', { params: { courseId, bookingId } }),
  getPendingThirdPartySessionCertificates: (sessionId: string) =>
    api.get<PendingThirdPartySessionCertificateDto[]>(`/sessions/${sessionId}/third-party-certificates/pending`),
  uploadSessionAttendeeCertificate: (sessionAttendeeId: string, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return api.post(`/session-attendees/${sessionAttendeeId}/certificate`, form)
  },
  getCertificateUrl: (trainingRecordId: string) =>
    api.get<{ url: string }>(`/training-records/${trainingRecordId}/certificate`),
  streamCertificate: (trainingRecordId: string) =>
    api.get(`/training-records/${trainingRecordId}/certificate/stream`, { responseType: 'blob' }),
  deleteCertificate: (trainingRecordId: string) =>
    api.delete(`/training-records/${trainingRecordId}/certificate`),
  regenerateCertificate: (trainingRecordId: string) =>
    api.post(`/training-records/${trainingRecordId}/certificate/regenerate`, {}),

  // Company profile
  getCompanyProfile: () => api.get<CompanyProfileDto>('/tenant-settings'),
  updateCompanyProfile: (data: Partial<CompanyProfileDto>) =>
    api.put('/tenant-settings', data),
  uploadTenantLogo: (file: File) => {
    const form = new FormData()
    form.append('file', file)
    return api.post<{ logoUrl: string }>('/tenant-settings/logo', form)
  },
  uploadTenantTerms: (file: File) => {
    const form = new FormData()
    form.append('file', file)
    return api.post<{ version: number }>('/tenant-settings/terms', form)
  },
  getTenantTermsDocument: (version?: number) =>
    api.get(`/tenant-settings/terms${version ? `?version=${version}` : ''}`, { responseType: 'blob' }),
  updateReminderSchedule: (data: { adminReminderDays: number[] | null; clientReminderDays: number[] | null }) =>
    api.put('/tenant-settings/reminders', data),
  updateCompanySignature: (data: { signatureType: SignatureType; signatureText: string | null }) =>
    api.put('/tenant-settings/signature', data),
  uploadCompanySignatureImage: (file: File) => {
    const form = new FormData()
    form.append('file', file)
    return api.post<{ url: string }>('/tenant-settings/signature/image', form)
  },
  updateFeedbackSettings: (data: { enabled: boolean; trainerVisible: boolean; allowEdit: boolean; windowDays: number | null; notifyClient: boolean }) =>
    api.put('/tenant-settings/feedback', data),

  // Analytics
  getAnalyticsOverview: (from: string | null, to: string | null) =>
    api.get<AnalyticsOverviewDto>('/analytics/overview', { params: { from, to } }),
  getCourseAnalytics: (from: string | null, to: string | null) =>
    api.get<CourseAnalyticsDto[]>('/analytics/courses', { params: { from, to } }),
  getTrainerAnalytics: (from: string | null, to: string | null) =>
    api.get<TrainerAnalyticsDto[]>('/analytics/trainers', { params: { from, to } }),
  getClientAnalytics: (from: string | null, to: string | null) =>
    api.get<ClientAnalyticsDto[]>('/analytics/clients', { params: { from, to } }),
  getFinanceAnalytics: (from: string | null, to: string | null) =>
    api.get<FinanceAnalyticsDto>('/analytics/finance', { params: { from, to } }),

  // Feedback
  getAllFeedback: () => api.get<BookingFeedbackDto[]>('/feedback'),
  getLiveInsights: (from: string | null, to: string | null, courseId: string | null) =>
    api.get<LiveSessionInsightDto[]>('/feedback/live', { params: { from, to, courseId } }),
  getTrainerLiveInsights: () => api.get<LiveSessionInsightDto[]>('/trainer-portal/live-feedback'),
  getBookingFeedback: (bookingId: string) => api.get<BookingFeedbackDto>(`/feedback/booking/${bookingId}`),
  getTrainerFeedback: () => api.get<BookingFeedbackDto[]>('/trainer-portal/feedback'),
  getTrainerFeedbackSettings: () => api.get<FeedbackSettingsDto>('/trainer-portal/feedback/settings'),

  // Trainer portal signature (TenantAdmin & Trainer)
  getTrainerSignature: () => api.get<TrainerSignatureDto>('/trainer-portal/me/signature'),
  updateTrainerSignature: (data: { signatureType: SignatureType; signatureText: string | null }) =>
    api.put('/trainer-portal/me/signature', data),
  uploadTrainerSignatureImage: (file: File) => {
    const form = new FormData()
    form.append('file', file)
    return api.post<{ url: string }>('/trainer-portal/me/signature/image', form)
  },

  // Venues
  getVenues: (clientId?: string) => api.get<VenueDto[]>('/venues', { params: clientId ? { clientId } : {} }),
  createVenue: (data: { name: string; addressLine1: string | null; addressLine2: string | null; city: string | null; county: string | null; postcode: string | null; notes: string | null; clientId: string | null }) =>
    api.post<VenueDto>('/venues', data),
  updateVenue: (id: string, data: { name: string; addressLine1: string | null; addressLine2: string | null; city: string | null; county: string | null; postcode: string | null; notes: string | null; clientId: string | null }) =>
    api.put(`/venues/${id}`, data),
  deleteVenue: (id: string) => api.delete(`/venues/${id}`),

  // Bookings
  getAllBookings: () => api.get<BookingListDto[]>('/bookings'),
  getBooking: (id: string) => api.get<BookingDto>(`/bookings/${id}`),
  getCalendarBookings: (year: number, month: number) =>
    api.get<CalendarBookingDto[]>(`/bookings/calendar?year=${year}&month=${month}`),
  getCalendarFeedToken: () => api.get<{ token: string | null }>('/calendar/feed-token'),
  generateCalendarFeedToken: () => api.post<{ token: string }>('/calendar/feed-token', {}),
  getClientBookings: (clientId: string) => api.get<BookingDto[]>(`/clients/${clientId}/bookings`),
  createBooking: (clientId: string, data: {
    courseId: string; bookingDate: string; startTime: string | null; endTime: string | null
    places: number; status: BookingStatus; notes: string | null; venueId: string | null
    attendeeIds: string[]; trainerAssignments: { trainerId: string; role: TrainerRole }[]
    additionalDays: { date: string; startTime: string | null; endTime: string | null }[]
    deliveryMethod: DeliveryMethod; meetingUrl: string | null; meetingInstructions: string | null
  }) => api.post<{ id: string }>(`/clients/${clientId}/bookings`, data),
  updateBooking: (id: string, data: {
    courseId: string; bookingDate: string; startTime: string | null; endTime: string | null
    places: number; status: BookingStatus; notes: string | null; venueId: string | null
    attendeeIds: string[]; trainerAssignments: { trainerId: string; role: TrainerRole }[]
    additionalDays: { date: string; startTime: string | null; endTime: string | null }[]
    deliveryMethod: DeliveryMethod; meetingUrl: string | null; meetingInstructions: string | null
  }) => api.put(`/bookings/${id}`, data),
  deleteBooking: (id: string) => api.delete(`/bookings/${id}`),
  updateBookingAttendance: (bookingId: string, attendeeId: string, status: string, outcome?: string | null, confirmRemoveCertificate = false) =>
    api.put(`/bookings/${bookingId}/attendees/${attendeeId}/attendance`, { status, outcome, confirmRemoveCertificate }),
  setBookingIdentityCheck: (bookingId: string, attendeeId: string, checked: boolean) =>
    api.put(`/bookings/${bookingId}/attendees/${attendeeId}/identity-check`, { checked }),
  getBookingActivity: (bookingId: string) => api.get<BookingActivityDto[]>(`/bookings/${bookingId}/activity`),
  completeBooking: (id: string, passingAttendeeIds: string[]) =>
    api.post(`/bookings/${id}/complete`, { passingAttendeeIds }),
  cancelBooking: (id: string, notifyClient: boolean) =>
    api.post(`/bookings/${id}/cancel`, { notifyClient }),
  getIssueCertificatesProgress: (id: string) =>
    api.get<{ done: number; total: number } | ''>(`/bookings/${id}/issue-certificates/progress`).then(r => r.data || null),
  issueCertificates: (id: string, missingOnly = false) =>
    api.post<{ trainingRecordsCreated: number; alreadyExisted: number; certificatesGenerated: number }>(`/bookings/${id}/issue-certificates`, undefined, { params: missingOnly ? { missingOnly: true } : undefined }),
  getBookingAttendeeCertificates: (id: string) =>
    api.get<BookingAttendeeCertificateDto[]>(`/bookings/${id}/certificates`),

  // Trainers
  getTrainers: () => api.get<TrainerDto[]>('/trainers'),
  getTrainer: (id: string) => api.get<TrainerDto>(`/trainers/${id}`),
  getTrainerBookings: (id: string) => api.get<TrainerBookingDto[]>(`/trainers/${id}/bookings`),
  getTrainerSeatInfo: () => api.get<TrainerSeatInfoDto>('/trainers/seats'),
  inviteTrainer: (id: string) => api.post(`/trainers/${id}/invite`, {}),
  deleteTrainer: (id: string) => api.delete(`/trainers/${id}`),
  createTrainer: (data: { firstName: string; lastName: string; email: string; phone: string | null; isActive: boolean; qualifiedCourseIds: string[] }) =>
    api.post<{ id: string }>('/trainers', data),
  updateTrainer: (id: string, data: { firstName: string; lastName: string; email: string; phone: string | null; isActive: boolean; qualifiedCourseIds: string[] }) =>
    api.put(`/trainers/${id}`, data),
  getTrainerConflicts: (id: string, date: string, excludeBookingId?: string) => {
    const params = new URLSearchParams({ date })
    if (excludeBookingId) params.set('excludeBookingId', excludeBookingId)
    return api.get<TrainerConflictDto[]>(`/trainers/${id}/conflicts?${params}`)
  },

  // Trainer unavailability (admin-managed, on a trainer's behalf)
  getTrainerUnavailability: (trainerId: string) =>
    api.get<TrainerUnavailabilityDto[]>(`/trainers/${trainerId}/unavailability`),
  addTrainerUnavailability: (trainerId: string, data: { startDate: string; endDate: string; reason: string | null }) =>
    api.post<TrainerUnavailabilityDto>(`/trainers/${trainerId}/unavailability`, data),
  addTrainerRecurringUnavailability: (trainerId: string, data: { daysOfWeek: DayOfWeekName[]; startDate: string; endDate: string | null; reason: string | null }) =>
    api.post<TrainerUnavailabilityDto[]>(`/trainers/${trainerId}/unavailability/recurring`, data),
  removeTrainerUnavailability: (trainerId: string, id: string) =>
    api.delete(`/trainers/${trainerId}/unavailability/${id}`),
  removeTrainerUnavailabilityGroup: (trainerId: string, groupId: string) =>
    api.delete(`/trainers/${trainerId}/unavailability/group/${groupId}`),

  // Invoices
  getAllInvoices: () => api.get<InvoiceListDto[]>('/invoices'),
  getInvoice: (id: string) => api.get<InvoiceDetailDto>(`/invoices/${id}`),
  createInvoice: (clientId: string, data: { bookingIds: string[]; dueDate: string | null; notes: string | null }) =>
    api.post<{ id: string }>(`/clients/${clientId}/invoices`, data),
  updateInvoice: (id: string, data: { paymentStatus: PaymentStatus; dueDate: string | null; notes: string | null }) =>
    api.put(`/invoices/${id}`, data),
  updateInvoiceBookings: (id: string, bookingIds: string[]) =>
    api.put(`/invoices/${id}/bookings`, { bookingIds }),
  sendInvoice: (id: string) => api.post(`/invoices/${id}/send`, {}),
  sendInvoiceReminder: (id: string, data: { subject: string; body: string }) =>
    api.post(`/invoices/${id}/send-reminder`, data),
  downloadInvoicePdf: (id: string) =>
    api.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
  bulkDownloadInvoicePdf: (ids: string[]) =>
    api.post('/invoices/bulk-pdf', { invoiceIds: ids }, { responseType: 'blob' }),
  sendClientExpiryWarning: (id: string, data: { subject: string; body: string }) =>
    api.post(`/clients/${id}/send-expiry-warning`, data),

  // Email templates
  getEmailTemplates: () => api.get<TenantEmailTemplateDto[]>('/tenant-settings/email-templates'),
  updateEmailTemplate: (type: string, data: { subject: string; body: string }) =>
    api.put(`/tenant-settings/email-templates/${type}`, data),

  // Account
  getNotificationPreferences: () => api.get<NotificationPreferencesDto>('/account/notifications'),
  updateNotificationPreferences: (data: NotificationPreferencesDto) => api.put('/account/notifications', data),
  changePassword: (data: { currentPassword: string; newPassword: string }) => api.post('/account/password', data),

  // Reports
  getAdminDashboard: () => api.get<AdminDashboardDto>('/reports/dashboard'),
  getComplianceReport: () => api.get<ComplianceReportDto[]>('/reports/compliance'),

  // Roster import
  importRoster: (file: File, dryRun: boolean) => {
    const fd = new FormData()
    fd.append('file', file)
    return api.post<ImportRosterResultDto>(`/import/roster?dryRun=${dryRun}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },

  // Booking enquiries
  getEnquiries: () => api.get<BookingEnquiryDto[]>('/enquiries'),
  updateEnquiryStatus: (id: string, status: EnquiryStatus, adminNotes?: string) =>
    api.patch(`/enquiries/${id}/status`, { status, adminNotes: adminNotes ?? null }),
  convertEnquiry: (id: string, data: {
    bookingDate: string; places: number; status: string; notes: string | null
    venueId: string | null; trainerAssignments: { trainerId: string; role: TrainerRole }[]
  }) => api.post<{ bookingId: string; clientId: string }>(`/enquiries/${id}/convert`, data),

  // Support requests
  getMySupportRequests: () => api.get<MySupportRequestDto[]>('/support/requests'),
  createSupportRequest: (data: { subject: string; message: string; category: SupportRequestCategory; pageUrl: string | null; phone?: string | null }) =>
    api.post<{ id: string }>('/support/requests', data),

  // Billing
  getBillingInfo: () => api.get<BillingInfo>('/billing').then(r => r.data),
  createBillingPortalSession: (returnUrl: string) =>
    api.post<{ url: string }>('/billing/portal-session', { returnUrl }).then(r => r.data.url),
  getAnnualSwitchPreview: () => api.get<AnnualSwitchPreview>('/billing/switch-to-annual/preview').then(r => r.data),
  switchToAnnual: () => api.post('/billing/switch-to-annual'),
  getCancellationOptions: () => api.get<CancellationOptions>('/billing/cancellation').then(r => r.data),
  cancelSubscription: (body: CancellationBody) =>
    api.post<{ accessEndsAt: string | null }>('/billing/cancellation/cancel', body).then(r => r.data),
  extendTrial: (body: CancellationBody) =>
    api.post<{ trialEndsAt: string }>('/billing/cancellation/extend-trial', body).then(r => r.data),
  applyRetentionDiscount: (body: CancellationBody) =>
    api.post('/billing/cancellation/apply-discount', body),
  reactivateSubscription: () => api.post('/billing/reactivate'),
  updateTrainerSeats: (addonSeats: number, prorationDate?: string) =>
    api.post<SubscriptionChangeResult>('/billing/trainer-seats', { addonSeats, prorationDate }).then(r => r.data),
  changePlan: (planTier: 'Starter' | 'Pro', prorationDate?: string) =>
    api.post<SubscriptionChangeResult>('/billing/change-plan', { planTier, prorationDate }).then(r => r.data),
  cancelPendingChange: () => api.post('/billing/pending-change/cancel'),
  getSeatChangePreview: (addonSeats: number) =>
    api.get<ChangePreview>('/billing/trainer-seats/preview', { params: { addonSeats } }).then(r => r.data),
  getPlanChangePreview: (planTier: 'Starter' | 'Pro') =>
    api.get<ChangePreview>('/billing/change-plan/preview', { params: { planTier } }).then(r => r.data),

  // Notifications
  getNotifications: (limit = 50) => api.get<AppNotificationDto[]>(`/notifications?limit=${limit}`),
  getUnreadNotificationCount: () => api.get<number>('/notifications/unread-count'),
  markNotificationRead: (id: string) => api.post(`/notifications/${id}/read`, {}),
  markAllNotificationsRead: () => api.post('/notifications/read-all', {}),

  // Sessions (open enrolment)
  getSessions: (status?: string) =>
    api.get<CourseSessionDto[]>('/sessions', { params: status ? { status } : {} }),
  getSession: (id: string) => api.get<CourseSessionDto>(`/sessions/${id}`),
  getSessionBookings: (id: string) => api.get<SessionBookingDto[]>(`/sessions/${id}/bookings`),
  createSession: (data: SessionRequest) => api.post<CourseSessionDto>('/sessions', data),
  updateSession: (id: string, data: SessionRequest) =>
    api.put(`/sessions/${id}`, data),
  deleteSession: (id: string) => api.delete(`/sessions/${id}`),
  cancelSessionBooking: (sessionId: string, bookingId: string) =>
    api.post(`/sessions/${sessionId}/bookings/${bookingId}/cancel`, {}),
  deleteSessionBooking: (sessionId: string, bookingId: string) =>
    api.delete(`/sessions/${sessionId}/bookings/${bookingId}`),
  updateSessionBookingNotes: (sessionId: string, bookingId: string, adminNotes: string | null) =>
    api.put(`/sessions/${sessionId}/bookings/${bookingId}/notes`, { adminNotes }),

  // Public attendees
  getAttendees: (search?: string) =>
    api.get<AttendeeListItemDto[]>('/attendees', { params: search ? { search } : {} }),
  getAttendee: (id: string) => api.get<AttendeeDetailDto>(`/attendees/${id}`),
  mergeAttendee: (targetId: string, sourceId: string) =>
    api.post(`/attendees/${targetId}/merge`, { sourceId }),

  // Session attendance + certificate issuance
  getSessionAttendees: (sessionId: string) =>
    api.get<SessionAttendeeDto[]>(`/sessions/${sessionId}/attendees`),
  setSessionAttendance: (sessionId: string, items: { sessionAttendeeId: string; attendanceStatus: AttendanceStatus; outcome?: AttendanceOutcome | null }[]) =>
    api.put(`/sessions/${sessionId}/attendance`, { items }),
  issueSessionCertificates: (sessionId: string, opts?: { missingOnly?: boolean; sendEmails?: boolean }) =>
    api.post<IssueSessionCertificatesResult>(`/sessions/${sessionId}/certificates`, { missingOnly: opts?.missingOnly ?? false, sendEmails: opts?.sendEmails ?? true }),
  addSessionAttendees: (sessionId: string, data: AddSessionAttendeesRequest) =>
    api.post<AddSessionAttendeesResult>(`/sessions/${sessionId}/attendees`, data),

  // Xero integration
  getXeroStatus: () =>
    api.get<{ isConnected: boolean; isConfigured: boolean; connection: XeroConnectionDto | null }>('/xero/status'),
  getXeroConnectUrl: () =>
    api.get<{ url: string }>('/xero/connect'),
  disconnectXero: () =>
    api.delete('/xero/disconnect'),
  updateXeroSettings: (accountCode: string, outputTaxType?: string | null) =>
    api.put('/xero/settings', { accountCode, outputTaxType }),
  pushInvoiceToXero: (invoiceId: string) =>
    api.post<{ xeroInvoiceId: string }>(`/xero/invoices/${invoiceId}/push`, {}),
  bulkPushInvoicesToXero: (invoiceIds: string[]) =>
    api.post<BulkPushResult>('/xero/invoices/push-bulk', { invoiceIds }),

  // Stripe Connect integration
  getStripeConnectStatus: () =>
    api.get<{ isConnected: boolean; isConfigured: boolean; connection: StripeConnectionDto | null }>('/stripe-connect/status'),
  startStripeOnboarding: () =>
    api.post<{ url: string }>('/stripe-connect/onboard', {}),
  disconnectStripeConnect: () =>
    api.delete('/stripe-connect/disconnect'),

  // Payments ledger + refunds
  getPayments: () => api.get<PaymentListDto[]>('/payments'),
  refundPayment: (paymentId: string, amount: number | null) =>
    api.post<{ refundedAmount: number; fullyRefunded: boolean }>(`/payments/${paymentId}/refund`, { amount }),

}
